import { CalendarDate } from "@internationalized/date";
import {
  ceilToStartOfDay,
  endOfContainingDay,
  type DateRange,
} from "@repo/app-types/entities/dateRange";
import { DateTime, Duration } from "luxon";
import { useMemo, useState, type FC } from "react";
import {
  Dialog,
  ListBox,
  ListBoxItem,
  RangeCalendar,
  type Key,
  type Selection,
} from "react-aria-components";
import { Button, ButtonVariant } from "../Buttons/button.js";
import { Size } from "../helpers/helpers.js";
import { useNow } from "../timestamps/timestampContext.js";
import {
  CalendarDateRangeInput,
  CalendarMonth,
  CalendarNavigationHeader,
} from "./calendar.js";

export interface DateRangeDialogProps {
  value: DateRange;
  onChange: (newValue: DateRange) => void;
}

type Preset = {
  key: Key;
  textValue: string;
  label: string;
  getValue: () => DateRange;
  checkIsSelected: (value: DateRange) => boolean;
};

const PRESETS: readonly Preset[] = [7, 14, 30, 90].map((days) => ({
  key: `last_${days}_days`,
  textValue: `${days}d`,
  label: `Last ${days} days`,
  getValue: () => ({
    length: Duration.fromObject({ days }),
    end: undefined,
  }),
  checkIsSelected: (value: DateRange) => {
    return (
      value.end === undefined && Math.round(value.length.as("days")) === days
    );
  },
}));

export const DateRangeDialog: FC<DateRangeDialogProps> = (props) => {
  const now = useNow();
  const [value, setValue] = useState<DateRange>(props.value);
  const [isChanged, setIsChanged] = useState(false);

  const selectedPreset = PRESETS.find((preset) =>
    preset.checkIsSelected(value),
  );
  const selectedPresetKey = selectedPreset?.key;
  const selectedPresetKeys = useMemo(
    () => (selectedPresetKey ? [selectedPresetKey] : []),
    [selectedPresetKey],
  );

  const handlePresetChange = (keys: Selection, close: () => void) => {
    const key = keys !== "all" ? keys.values().next().value : undefined;
    const preset = PRESETS.find((p) => p.key === key);
    if (preset) {
      const value = preset.getValue();
      props.onChange(value);
      close();
    }
  };

  const maxCalendarValue = useMemo(
    () => createCalendarDateFromDateTime(now.endOf("day"), "endOfDay"),
    [now],
  );

  const calendarValue = useMemo(() => {
    let startDateTime, endDateTime;

    if (value.end) {
      startDateTime = value.end.minus(value.length);
      endDateTime = value.end;
    } else {
      startDateTime = now.minus(value.length);
      endDateTime = now;
    }

    return {
      start: createCalendarDateFromDateTime(startDateTime, "startOfDay"),
      end: createCalendarDateFromDateTime(endDateTime, "endOfDay"),
    };
  }, [now, value]);

  const handleCalendarChange = (newCalendarValue: {
    start: CalendarDate;
    end: CalendarDate;
  }) => {
    // Convert to the start of the day in the local time zone
    const startDateTime = createDateTimeFromCalendarDate(
      newCalendarValue.start,
      "startOfDay",
    );
    // Convert to the end of the day in the local time zone
    const endDateTime = createDateTimeFromCalendarDate(
      newCalendarValue.end,
      "endOfDay",
    );
    // Calculate the period length. Round to whole days to remove any artifacts from DST changes, which might otherwise cause unnatural behavior when we use it for later computations.
    const length = endDateTime
      .diff(startDateTime, "days")
      .mapUnits((x) => Math.round(x));

    if (length.isValid) {
      const value = {
        length,
        end: endDateTime,
      };

      setValue(value);
      setIsChanged(true);
    }
  };

  const handleApplyClick = (close: () => void) => {
    props.onChange(value);
    close();
  };

  return (
    <Dialog className="grid grid-cols-[auto_auto] grid-rows-[auto_auto]">
      {({ close }) => (
        <>
          <div className="row-span-full border-r border-border-light py-1">
            <ListBox
              aria-label="Preset date ranges"
              className="text-sm"
              selectionMode="single"
              selectedKeys={selectedPresetKeys}
              onSelectionChange={(selection) =>
                handlePresetChange(selection, close)
              }
            >
              {PRESETS.map((preset) => (
                <ListBoxItem
                  id={preset.key}
                  key={preset.key}
                  textValue={preset.textValue}
                  className="py-2 pr-6 pl-3 text-sm text-foreground select-none hover:bg-gray-100 selected:bg-primary selected:text-primary-foreground"
                >
                  {preset.label}
                </ListBoxItem>
              ))}
            </ListBox>
          </div>

          <div className="p-4">
            <RangeCalendar
              aria-label="Date range"
              visibleDuration={{ months: 2 }}
              value={selectedPreset ? null : calendarValue}
              onChange={handleCalendarChange}
              pageBehavior="single"
              maxValue={maxCalendarValue}
              defaultFocusedValue={calendarValue.end}
              selectionAlignment="end"
            >
              <CalendarNavigationHeader />

              <div className="flex items-start gap-4">
                {[0, 1].map((month) => (
                  <CalendarMonth
                    key={month}
                    offsetMonths={month}
                    selectionMode="range"
                  />
                ))}
              </div>
            </RangeCalendar>
          </div>

          <div className="flex items-center justify-between gap-4 px-4 pb-4">
            <CalendarDateRangeInput
              value={{
                start: selectedPreset ? null : calendarValue.start,
                end: selectedPreset ? null : calendarValue.end,
              }}
              isRequired={!selectedPreset}
              maxValue={maxCalendarValue}
              onStartChange={(start) =>
                handleCalendarChange({ ...calendarValue, start })
              }
              onEndChange={(end) =>
                handleCalendarChange({ ...calendarValue, end })
              }
              // Enter only applies an edited range; otherwise it falls
              // through to the dialog.
              onEnter={isChanged ? () => handleApplyClick(close) : undefined}
            />

            <div className="flex items-center gap-2">
              <Button
                size={Size.MEDIUM}
                variant={ButtonVariant.Primary}
                isDisabled={!isChanged}
                onClick={() => handleApplyClick(close)}
              >
                Apply
              </Button>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
};

// Mirrors the query-side snapping (both bounds ceil to day boundaries) so
// the calendar highlights the same days the range actually covers.
function createCalendarDateFromDateTime(
  dateTime: DateTime<true>,
  affinity: "startOfDay" | "endOfDay",
): CalendarDate {
  const snapped =
    affinity === "startOfDay"
      ? ceilToStartOfDay(dateTime)
      : endOfContainingDay(dateTime);

  return new CalendarDate(snapped.year, snapped.month, snapped.day);
}

function createDateTimeFromCalendarDate(
  date: CalendarDate,
  affinity: "startOfDay" | "endOfDay",
): DateTime {
  const baseDateTime = DateTime.fromObject({
    year: date.year,
    month: date.month,
    day: date.day,
  });

  return affinity === "startOfDay"
    ? baseDateTime.startOf("day")
    : baseDateTime.endOf("day");
}
